import * as Sentry from "@sentry/react";
import { fireArtifactAction } from "@/components/artifacts/ArtifactActionsButtons.tsx";
import { BrowserSignInBiscuit } from "@/components/artifacts/BrowserSignInBiscuit.tsx";
import {
  isArtifactReadOnly,
  isBrowserSignInArtifact,
} from "@/components/artifacts/artifactUtils.ts";
import { SummaryCard } from "@/components/artifacts/ArtifactSummaryCard.tsx";
import { HeavyArtifactLightbox } from "@/components/artifacts/HeavyArtifactLightbox.tsx";
import { omniArtifactBody } from "@/components/artifacts/omni/omniArtifactBody.tsx";
import { useCanvasFollowsCarousel } from "@/components/artifacts/useCanvasFollowsCarousel.ts";
import { useLightboxUrlState } from "@/components/artifacts/useLightboxUrlState.ts";
import { ArtifactView } from "@/components/artifacts/view/ArtifactView.tsx";
import { useCanvasActionContext } from "@/components/canvas/canvasContext.tsx";
import type { StagedDecision } from "@/shared/artifactDecisions.ts";
import { Button } from "@/components/ui/button.tsx";
import { OmniDismissButton } from "@/components/artifacts/OmniDismissButton.tsx";
import {
  OmniCardChrome,
  OmniCardExpandButton,
  type OmniCardExpand,
  OmniCardHeader,
  type OmniCardPagination,
  type OmniCardStaging,
} from "@/components/artifacts/OmniCard.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { useArtifact } from "@/hooks/useArtifacts.tsx";
import { messageQueries } from "@/shared/messageQueries.ts";
import { useIsSharedThread } from "@/hooks/useIsSharedThread.ts";
import { toUrnString } from "@/types/urn.ts";
import type { ArtifactAction, HeavyArtifactResponse } from "@/types/artifacts.ts";
import { artifactDisplayTitle } from "@/shared/artifactTitle.ts";
import { cn } from "@/lib/utils.ts";
import { Check, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Fragment,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

/**
 * A row in the biscuit's stacked column: its hover band, drawn as a
 * pseudo-element inside the row's own stacking context so it takes no layout
 * space. `-inset-y-px` is the reach that takes in the rule above the row and the
 * one below it, and `-z-10` puts the band in front of both.
 */
export const OMNI_ROW_BAND_CLASS =
  "min-h-[56px] relative isolate before:absolute before:-inset-y-px before:inset-x-0 before:-z-10 before:rounded-sm before:transition-colors hover:before:bg-muted/50";

/**
 * The rule a row draws above itself, starting at the badge's leading edge rather
 * than the card's and symmetric on the right, plus the hover that clears it.
 *
 * The band is half-opaque, so a rule left underneath it would read through at
 * half strength rather than disappear. A hovered row therefore switches off both
 * rules its band reaches: its own via `hover:after:`, and the one belonging to
 * the row below via the `[&:hover+*]:after:` that the biscuit's action rows add.
 * That sibling selector only finds a rule declared exactly like this one, so
 * every row in the column has to share this string — a copy that drifts stops
 * being switched off, and the band starts getting cut by a line again.
 */
export const OMNI_ROW_RULE_CLASS =
  "after:absolute after:inset-x-2.5 after:top-0 after:-z-20 after:border-t after:border-border hover:after:border-transparent";

/**
 * Omnibox-overlay biscuit shown when the most recent thread item is a heavy
 * artifact awaiting user action. Renders the server-emitted `artifact.biscuit`
 * (title, optional icon+subtitle header, labeled rows, body excerpt) plus
 * `artifact.actions[]` as numbered action rows. The `followUp` slot is the
 * regular Omnibox restyled to read as a third action row ("Ask a follow up").
 *
 * Visual idiom matches the designer's overlay prototype: heading
 * outside the card, a bordered preview card with field rows, then full-width
 * action rows with `border-t` dividers between them and square badges on the
 * left. Body excerpts are rendered DOMPurify-sanitized when the server
 * emits `format: "html"`.
 */
export const ArtifactBiscuit = ({
  artifact: msgListArtifact,
  threadId,
  followUp,
  fallback,
  pagination,
  staging,
  onDismiss,
  onOpenedOnCanvas,
  browserControlRef,
}: {
  artifact: HeavyArtifactResponse;
  threadId: number;
  followUp?: ReactNode;
  /**
   * What to render in place of the card when there is no biscuit to draw.
   *
   * The caller's `followUp` is the thread's only text box, and it is a child of
   * this component — so returning a bare `null` takes the composer with it
   * (BIG-7439). A caller that hands over its composer has to hand over
   * somewhere for it to land too.
   *
   * Whether this card draws is not knowable from outside: the artifact rendered
   * here is resolved from the artifact cache, which by design outranks the
   * thread snapshot the caller selected from (see `useArtifact`). So a caller
   * checking its own copy can be right and still be surprised, and the decision
   * has to carry its own fallback rather than be predicted.
   */
  fallback?: ReactNode;
  /**
   * Carousel controls, present only when more than one artifact is surfaced.
   * Its presence also enables left/right arrow-key navigation.
   */
  pagination?: OmniCardPagination;
  /**
   * Review-queue mode (present only when ≥2 artifacts are surfaced). Clicking an
   * action stages it rather than firing it, so the whole review submits as one
   * request; `decision` is what this card has staged, and the row it was chosen
   * on says so. The queue owner decides what follows a stage — next card, or a
   * commit if this is the last one.
   */
  staging?: OmniCardStaging & {
    decision?: StagedDecision;
    /** Stage this card's action into the batch and move the review along. */
    onStageAction: (action: ArtifactAction) => void;
  };
  /** Escape, or the header X, dismisses the overlay (throws the queue away).
   *  The key is handled at the capture phase so it can't bubble to a
   *  back-navigation handler. */
  onDismiss?: () => void;
  /**
   * The canvas took the artifact, so the full view is open beside this card.
   * A host that covers the canvas, like a dialog, gets out of its way here;
   * the lightbox fallback opens over the host and needs nothing from it.
   */
  onOpenedOnCanvas?: () => void;
  browserControlRef?: Ref<HTMLDivElement>;
}) => {
  // The thread snapshot this card arrives on is only a starting point: the
  // lightbox below is a fully editable view, and a field edit writes the
  // refreshed artifact to the artifact cache, not back into the snapshot.
  // Subscribing to that cache (as the in-thread card does) is what lets an
  // edit made here re-render here.
  const queryClient = useQueryClient();
  const { artifact: artifactData } = useArtifact({
    threadId,
    messageId: msgListArtifact.messageId,
    artifact: msgListArtifact,
  });
  const artifact = artifactData as HeavyArtifactResponse;

  const biscuit = artifact.biscuit;
  const actions = artifact.actions ?? [];
  // In queue mode key the lightbox by a stable token (not `artifact.id`) so it
  // stays open as the active artifact changes — navigating swaps the view
  // instead of closing the dialog.
  const [open, setOpen] = useLightboxUrlState("biscuit", pagination ? "queue" : artifact.id);
  const isSharedThread = useIsSharedThread();
  const canvas = useCanvasActionContext();
  const artifactUrn = toUrnString({ type: artifact.artifactType, id: artifact.id });
  const isBrowserSignIn = isBrowserSignInArtifact(artifact);

  /**
   * Open the artifact behind the summary card on the canvas — the single place
   * an artifact opens — mirroring the in-thread chip (`HeavyArtifactItem`). The
   * canvas is undefined on mobile / gate-off and `resetTo` declines URNs it
   * can't render, so both fall through to the in-thread lightbox.
   *
   * A queued artifact presents on the canvas too, but without its action footer:
   * `usePublishArtifactReviewQueue` marks it under review so the canvas won't
   * offer to fire a decision that has to stage into the batch instead.
   */
  const openArtifact = () => {
    if (canvas?.resetTo(artifactUrn)) {
      onOpenedOnCanvas?.();
      return;
    }
    setOpen(true);
  };

  useCanvasFollowsCarousel(artifactUrn, canvas, isBrowserSignIn ? "inline" : "canvas");

  /**
   * Artifact urn → the id of the action it has in flight, so that action's row
   * can say so and the rest of its card locks out.
   *
   * A map rather than one slot, because the caller renders this component
   * without a `key`: switching cards reuses this instance while the request the
   * previous card started is still open. One slot let the second card overwrite
   * the first card's lock, and let whichever request settled first release a
   * lock belonging to the other — so a card could be fired twice while its own
   * POST was still outstanding, duplicating a send.
   *
   * The ref is what the digit shortcuts read; the state is what the rows render.
   */
  const [firing, setFiring] = useState<Record<string, string>>({});
  const firingRef = useRef<Record<string, string>>({});
  const firingActionId = firing[artifactUrn];

  /**
   * A decided card normally leaves the feed, retiring its lock with it. One that
   * advances instead keeps its urn and offers a different action: approving a
   * subscription comes back `authenticationRequired`, which is still
   * `waitingForUserAction`, now asking the user to finish at the merchant.
   *
   * The lock exists to stop the action it was taken for from being fired twice,
   * so once that action is no longer on offer it has done its job. Holding it
   * would leave every button and shortcut on the advanced card dead.
   */
  const firedActionStillOffered = actions.some((a) => a.id === firingActionId);
  useEffect(() => {
    if (firingActionId === undefined || firedActionStillOffered) return;
    const released = { ...firingRef.current };
    delete released[artifactUrn];
    firingRef.current = released;
    setFiring(released);
  }, [artifactUrn, firingActionId, firedActionStillOffered]);

  /** Fires the action, reporting whether it landed. */
  const runAction = useCallback(
    async (action: ArtifactAction) => {
      try {
        await fireArtifactAction(action);
        // Refresh the feed the card was read from, so this row and the inline
        // message list stop showing an artifact that has since been decided
        // (BIG-7171). The canvas re-reads the artifact by id and would otherwise
        // be the only surface telling the truth.
        await queryClient.invalidateQueries({ queryKey: messageQueries.thread(threadId) });
        return true;
      } catch (error) {
        Sentry.captureException(error);
        errorToast(error instanceof Error ? error.message : "Action failed.");
        return false;
      }
    },
    [queryClient, threadId],
  );

  // Under review a click stages the action into the batch; alone, it fires. A
  // plain function (not memoized) read via the shortcut ref, so the digit
  // shortcuts and the row clicks share one path.
  //
  // GET_EXTERNAL is the exception on both counts: it is a browser redirect the
  // server cannot perform on the user's behalf (the batch endpoint rejects it
  // outright), and it decides nothing — the artifact is still waiting when the
  // user comes back, so the review stays on it.
  const activate = (action: ArtifactAction) => {
    if (isBrowserSignIn && isSharedThread) return;
    if (staging?.isSubmitting) return;
    // Nothing on this surface moves until the POST and the refetch both land,
    // and the server mints a fresh id per request rather than deduping, so an
    // unguarded second click sends the same draft twice (BIG-7171).
    if (firingRef.current[artifactUrn]) return;
    if (staging && action.method === "POST" && !action.reusable) {
      staging.onStageAction(action);
      return;
    }
    // Captured, so the release below names the artifact this request belongs to
    // rather than whichever card is on screen when it settles.
    const firedUrn = artifactUrn;
    firingRef.current = { ...firingRef.current, [firedUrn]: action.id };
    setFiring(firingRef.current);
    void runAction(action).then((succeeded) => {
      // A settled POST takes its card with it, so that artifact's lock stays on.
      // Anything else leaves the row in play and has to release it — only its
      // own, since another card may still be mid-flight.
      if (!succeeded || action.method !== "POST" || action.reusable) {
        const released = { ...firingRef.current };
        delete released[firedUrn];
        firingRef.current = released;
        setFiring(released);
        return;
      }
      // Deciding the card is done with it, so the canvas presenting it goes too
      // — otherwise Apply changes / Discard leaves the user staring at the thing
      // they just settled. The artifact that fired, not whichever is on screen
      // by the time its request lands.
      canvas?.dropContent(firedUrn);
    });
  };

  // Lock keyboard navigation while the batch is committing (buttons already
  // disable; this guards the document-level digit/arrow listeners too).
  const submittingRef = useRef(false);
  submittingRef.current = staging?.isSubmitting ?? false;

  // Keyboard shortcut: "1"/"2"/… while no input is focused (or while focused
  // but empty) triggers the matching action row. Latest-value ref so the
  // document listener doesn't need to re-attach on every render.
  const isWaitingForAction = artifact.status === "waitingForUserAction";
  const shortcutStateRef = useRef({
    isWaiting: isWaitingForAction,
    actions,
    activate,
    isLightboxOpen: open,
  });
  shortcutStateRef.current = {
    isWaiting: isWaitingForAction,
    actions,
    activate,
    isLightboxOpen: open,
  };
  const hasActions = actions.length > 0 && isWaitingForAction && !isBrowserSignIn;
  useEffect(() => {
    if (!hasActions) return;
    const handler = (e: KeyboardEvent) => {
      const state = shortcutStateRef.current;
      if (!state.isWaiting) return;
      // Bail when the fullscreen editor is open — the user is in the middle
      // of editing the artifact; numeric keypresses there are body content,
      // not action shortcuts. Mirrors the contentEditable check below.
      if (state.isLightboxOpen) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Never hijack digits from a text input, even an empty one: fields like
      // addresses legitimately begin with a digit ("1600 Amphitheatre…"), so
      // the first keystroke must be literal. The follow-up textarea keeps its
      // empty-box shortcut so the numbered actions stay reachable while it
      // holds the card's autofocus.
      const target = e.target;
      if (target instanceof HTMLInputElement) return;
      if (target instanceof HTMLTextAreaElement && target.value.length > 0) return;
      // Rich-text editors (ArtifactEditor / markdown) use `contentEditable`
      // divs, which `target instanceof HTMLInputElement` doesn't catch.
      // Always bail when the focus is inside an editable element so a
      // numeric keystroke can't fire the action while the user is
      // composing the body.
      if (target instanceof HTMLElement && target.isContentEditable) return;
      const idx = Number.parseInt(e.key, 10);
      if (!Number.isInteger(idx) || idx < 1 || idx > state.actions.length) return;
      const action = state.actions[idx - 1];
      if (!action) return;
      e.preventDefault();
      state.activate(action);
    };
    document.addEventListener("keydown", handler, { capture: true });
    return () => document.removeEventListener("keydown", handler, { capture: true });
  }, [hasActions]);

  // Left/right arrow steps the carousel. Latest-value ref so the document
  // listener attaches once (only while carousel controls exist) rather than
  // re-binding as the inline `pagination` object changes identity each render.
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;
  const hasPagination = !!pagination;
  useEffect(() => {
    if (!hasPagination) return;
    const handler = (e: KeyboardEvent) => {
      const p = paginationRef.current;
      if (!p || submittingRef.current) return;
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Arrows move the caret inside any editable field — never hijack those.
      const target = e.target;
      if (
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLInputElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      if (e.key === "ArrowLeft") p.onPrev();
      else p.onNext();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [hasPagination]);

  if (!biscuit) return <>{fallback}</>;

  if (isBrowserSignIn) {
    return (
      <BrowserSignInBiscuit
        artifact={artifact}
        actions={isWaitingForAction ? actions : []}
        onAction={activate}
        disabled={isSharedThread || staging?.isSubmitting}
        pagination={pagination}
        staging={staging}
        onDismiss={onDismiss}
        followUp={followUp}
        browserControlRef={browserControlRef}
      />
    );
  }

  // A card the user acts *in* brings its own middle — see `omniArtifactBody`.
  // Everything else here is unchanged by it.
  const customBody = omniArtifactBody(artifact, threadId);
  const hasSummary = biscuit.subtitle || biscuit.fields?.length || biscuit.excerpt;
  // Server-driven accent tint (e.g. Instant Book kickoffs). Generic — any
  // artifact can opt in via `summary.tone`; no per-type branching here. The
  // hover stays within the same hue so it doesn't wash the accent back to grey.
  const isAccent = biscuit.tone === "accent";
  const toneClass = isAccent ? "bg-primary/5 border-primary/20" : "bg-popover border-border";
  const toneHover = isAccent ? "hover:bg-primary/10" : "hover:bg-muted/30";
  const isReadOnly = isArtifactReadOnly(artifact, isSharedThread);
  // Only allow opening the fullscreen lightbox when there's a `view` payload
  // to render inside it — without `view` the dialog body would be blank.
  // Today `mapArtifact` always stamps `view` + `biscuit` together, so this
  // mostly guards against a future regression or a stale response.
  const canOpenFullscreen = Boolean(artifact.view);
  const stagedDecision = staging?.decision;

  /* Sits beside the dismiss in whichever header this card has: the two are the
     same kind of thing, what to do with this card as a whole.

     The header rather than the preview card, because the card is not always
     there to put it on. `omniArtifactBody` returns a bespoke body for every
     picker of kind connectors, calendars, locations, protectedMeetings or
     routines, and those never render the clickable summary card at all, so an
     affordance placed inside it would be missing from exactly the artifacts
     that most need one. */
  const expand: OmniCardExpand | undefined = canOpenFullscreen
    ? { onExpand: openArtifact, label: `Expand ${artifactDisplayTitle(artifact)}` }
    : undefined;

  return (
    <>
      <OmniCardChrome onDismiss={onDismiss}>
        {/* Only when more than one artifact is surfaced. Without a queue there
            is no such row, and the controls ride the title line instead. */}
        {pagination && (
          <OmniCardHeader
            pagination={pagination}
            staging={staging}
            expand={expand}
            onDismiss={onDismiss}
            statsigPrefix="artifact-biscuit"
          />
        )}

        {/* Note staged for this card — actions show their staged state on the
            row itself; a note has no row, so surface it here. */}
        {stagedDecision?.kind === "note" && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Check className="size-3.5 shrink-0" />
            Note added — sent when you finish the queue
          </div>
        )}

        {/* Title + preview card group, held apart by a gap proportionate to the
            title's own line height so the two read as one unit. */}
        <div className="flex flex-col gap-3.5">
          <div className="flex items-start justify-between gap-2 ps-[5px]">
            {/* The treatment a thread's own title wears (`panelHeaderCrumbTitleClass`,
                and the rename input beside it). This card is a step inside a
                conversation, not a page of its own, so it reads at the same
                weight as the thread it belongs to. */}
            <div className="text-base font-medium leading-5">{biscuit.title}</div>
            {/* A queue keeps its controls up in the pager; without one the title
                line is the header, so they sit at the end of it. */}
            {!pagination && (expand || onDismiss) && (
              <div className="flex shrink-0 items-center gap-1">
                {expand && (
                  <OmniCardExpandButton
                    expand={expand}
                    disabled={staging?.isSubmitting}
                    statsigName="artifact-biscuit-expand"
                  />
                )}
                {onDismiss && (
                  <OmniDismissButton
                    onDismiss={onDismiss}
                    disabled={staging?.isSubmitting}
                    statsigName="artifact-biscuit-dismiss"
                  />
                )}
              </div>
            )}
          </div>

          {/* Keyed by artifact: the biscuit instance deliberately survives paging,
              so without this the body a card pages to inherits the offset the
              previous card was scrolled to and opens part-way down itself. */}
          {customBody ? <Fragment key={artifact.id}>{customBody}</Fragment> : null}

          {!customBody &&
            hasSummary &&
            (canOpenFullscreen ? (
              <Button
                type="button"
                variant="blank"
                onClick={openArtifact}
                className={cn(
                  "w-full h-auto rounded-md border p-0 flex-col items-stretch justify-start text-left font-normal whitespace-normal",
                  toneClass,
                  toneHover,
                )}
                aria-label={`Open ${artifactDisplayTitle(artifact)}`}
                data-statsig-name="artifact-biscuit-open-fullscreen"
              >
                <SummaryCard key={artifact.id} summary={biscuit} />
              </Button>
            ) : (
              <div className={cn("rounded-md border flex flex-col", toneClass)}>
                <SummaryCard key={artifact.id} summary={biscuit} />
              </div>
            ))}
        </div>

        {/* Action rows and follow-up read as one stacked column, each row ruled
            off from the one above it. The negative margins keep the column flush
            with the card: `-mt` absorbs the parent `gap-3` plus the row's own
            centring so the first label sits ~8px below the preview card, and
            `-mb` does the same at the bottom.
            The "Ask a follow up" omnibox uses `hideInputControls` and a sparkle
            leading badge so the textarea sits flush as a peer row. */}
        {isWaitingForAction && actions.length > 0 ? (
          // Every row in one column: the action rows and the follow-up keep one
          // rhythm, each draws its own top rule, and a hovered row is a sibling of
          // the row below it, which is what lets it switch that row's rule off.
          <div className="-mt-[6px] -mb-[7px] flex flex-col">
            {actions.map((action, idx) => {
              // The staged row wears a check in place of its number: the click
              // did land, it just hasn't left the client yet. Without this the
              // card looks untouched and the user clicks Send again (BIG-7171).
              const isStaged =
                stagedDecision?.kind === "action" && stagedDecision.action.id === action.id;
              // Same reason as the staged check: the click landed, and this row
              // is the only thing that can say so before the card moves.
              const isFiring = firingActionId === action.id;
              return (
                <Button
                  key={action.id}
                  type="button"
                  variant="blank"
                  onClick={() => activate(action)}
                  disabled={staging?.isSubmitting || firingActionId !== undefined}
                  aria-pressed={staging ? isStaged : undefined}
                  data-statsig-name={action.statsigName ?? "artifact-biscuit-action"}
                  className={cn(
                    "w-full h-auto justify-start rounded-sm py-0 pl-2.5 pr-4 text-left text-sm font-normal",
                    "border-0",
                    OMNI_ROW_BAND_CLASS,
                    // Kills the rule belonging to the row below this one, which
                    // the band also reaches.
                    "[&:hover+*]:after:border-transparent",
                    // The first row has nothing above it to rule off from.
                    idx > 0 && OMNI_ROW_RULE_CLASS,
                  )}
                >
                  <span
                    className={cn(
                      "size-8 shrink-0 flex items-center justify-center rounded-sm text-sm font-medium",
                      isStaged
                        ? "bg-primary text-primary-foreground"
                        : "bg-accent text-accent-foreground",
                    )}
                  >
                    {isFiring && <Loader2 className="size-4 animate-spin" />}
                    {!isFiring && (isStaged ? <Check className="size-4" /> : idx + 1)}
                  </span>
                  {action.label}
                  {isStaged && (
                    <span className="ml-auto text-xs text-muted-foreground">
                      Queued — sent when you finish
                    </span>
                  )}
                </Button>
              );
            })}
            {followUp}
          </div>
        ) : (
          followUp && <div className="-mb-[7px] flex flex-col">{followUp}</div>
        )}
      </OmniCardChrome>

      <HeavyArtifactLightbox
        artifact={artifact}
        threadId={threadId}
        messageId={artifact.messageId}
        open={open}
        onOpenChange={setOpen}
        onStage={staging?.onStageAction}
        stagedAction={stagedDecision?.kind === "action" ? stagedDecision.action : undefined}
      >
        {artifact.view ? (
          <ArtifactView
            view={artifact.view}
            threadId={threadId}
            messageId={artifact.messageId}
            artifactId={artifact.id}
            isReadOnly={isReadOnly}
            isSharedThread={isSharedThread}
            maximized
          />
        ) : null}
      </HeavyArtifactLightbox>
    </>
  );
};
